import { useCallback, useState } from "react";
import { toast } from "@jsq/ds/core";
import {
  useCreateBillingClientOverviewExportMutation,
  useCreateBillingClientArenaExportMutation,
} from "~/graphql/supergraphHooks";
import {
  type OverviewFilters,
  type OverviewSort,
  type ClientArenaFilter,
  type ClientArenaSort,
} from "~/graphql/supergraphTypes";

interface UseBillingExportParams {
  onJobStatusChange?: (isProcessing: boolean) => void;
}

interface TriggerOverviewExportParams {
  exportType: "client_overview";
  filters: OverviewFilters[] | null;
  sorts?: OverviewSort[] | null;
  asOfDate: string;
}

interface TriggerDetailsExportParams {
  exportType: "client_arena";
  clientId: string;
  filters?: ClientArenaFilter[] | null;
  sorts?: ClientArenaSort[] | null;
  asOfDate: string;
}

type TriggerExportParams = TriggerOverviewExportParams | TriggerDetailsExportParams;

interface UseBillingExportReturn {
  triggerExport: (params: TriggerExportParams) => Promise<void>;
  isExporting: boolean;
  modalProps: {
    showGeneratingModal: boolean;
    closeGeneratingModal: () => void;
    exportId: string | null;
    onJobStatusChange: (inProgress: boolean) => void;
  };
}

export const useBillingExport = (params?: UseBillingExportParams): UseBillingExportReturn => {
  const { onJobStatusChange } = params || {};

  const [showModal, setShowModal] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [isJobProcessing, setIsJobProcessing] = useState(false);

  const [createOverviewExport, { loading: overviewLoading }] =
    useCreateBillingClientOverviewExportMutation();
  const [createDetailsExport, { loading: detailsLoading }] =
    useCreateBillingClientArenaExportMutation();

  const isMutationLoading = overviewLoading || detailsLoading;

  const handleJobStatusChange = useCallback(
    (inProgress: boolean) => {
      setIsJobProcessing(inProgress);
      onJobStatusChange?.(inProgress);
    },
    [onJobStatusChange],
  );

  const handleExportError = useCallback(
    (message: string) => {
      toast.error(message);
      setJobId(null);
      setShowModal(true);
      handleJobStatusChange(false);
    },
    [handleJobStatusChange],
  );

  const triggerExport = useCallback(
    async (triggerParams: TriggerExportParams) => {
      // Early return if already loading or processing
      if (isMutationLoading || isJobProcessing) {
        return;
      }

      let exportJobId: string | null = null;
      let mutationResult: {
        data?: {
          createBillingClientOverviewExport?: string | null;
          createBillingClientArenaExport?: string | null;
        } | null;
        errors?: readonly { message: string }[];
      } | null = null;

      try {
        // Execute the appropriate mutation based on export type
        if (triggerParams.exportType === "client_overview") {
          mutationResult = await createOverviewExport({
            variables: {
              asOfDate: triggerParams.asOfDate,
              filters: triggerParams.filters,
              sorts: triggerParams.sorts,
            },
          });
          exportJobId = mutationResult.data?.createBillingClientOverviewExport as string | null;
        } else if (triggerParams.exportType === "client_arena") {
          mutationResult = await createDetailsExport({
            variables: {
              clientId: triggerParams.clientId,
              asOfDate: triggerParams.asOfDate,
              filters: triggerParams.filters,
              sorts: triggerParams.sorts,
            },
          });
          exportJobId = mutationResult.data?.createBillingClientArenaExport as string | null;
        }

        // Handle GraphQL errors
        const graphQLErrors = mutationResult?.errors;
        if (graphQLErrors && graphQLErrors.length > 0) {
          const errorMessage = graphQLErrors[0]?.message || "Error starting export job.";
          handleExportError(errorMessage);
        }
        // Handle missing job ID
        else if (!exportJobId) {
          handleExportError(
            "Failed to start export job. No Job ID returned. Please try again.",
          );
        }
        // Success case
        else {
          setJobId(exportJobId);
          setShowModal(true);
          handleJobStatusChange(true);
        }
      } catch (error: unknown) {
        // Handle unexpected errors
        let errorMessage = "An error occurred while starting the export.";
        if (error instanceof Error) {
          errorMessage = error.message;
        }
        handleExportError(errorMessage);
      }
    },
    [
      isMutationLoading,
      isJobProcessing,
      createOverviewExport,
      createDetailsExport,
      handleJobStatusChange,
      handleExportError,
    ],
  );

  const closeModal = useCallback(() => {
    setShowModal(false);
    setJobId(null);
    handleJobStatusChange(false);
  }, [handleJobStatusChange]);

  const isExporting = isMutationLoading || isJobProcessing;

  const modalProps = {
    showGeneratingModal: showModal,
    closeGeneratingModal: closeModal,
    exportId: jobId,
    onJobStatusChange: handleJobStatusChange,
  };

  return {
    triggerExport,
    isExporting,
    modalProps,
  };
};
